import React, { useState, useEffect, useCallback } from 'react';
import Breadcrumbs from './components/Breadcrumbs';
import PlayerCard from './components/PlayerCard';
import TabRow from './components/TabRow';
import BasicDetailsTab from './components/BasicDetailsTab';
import ArtistsOwnershipTab from './components/ArtistsOwnershipTab';
import AlbumsLabelsTab from './components/AlbumsLabelsTab';
import DistributionTab from './components/DistributionTab';
import RingtoneTab from './components/RingtoneTab';
import VersionsTab from './components/VersionsTab';
import CasesNotesTab from './components/CasesNotesTab';
import ConflictsTab from './components/ConflictsTab';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import { toast } from '../../components/shared/toast';
import { apiClient } from '../../api/apiClient';
import { GET_SONGS_URL, CHECK_SONG_NAME_URL, INACTIVATE_CHECK_SONG_URL, INACTIVATE_SONG_URL, ACTIVATE_SONG_URL } from '../../api/song';import { createDistributorOption } from '../Distributor/utils/distributorOptions';import en from '../../translations/en.json';
import './SongDetails.css';

function SongDetails({ song, onBack, onSelectAlbum, onSelectCase }) {
  const [detailedSong, setDetailedSong] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('Basic Details');
  const [hasActiveConflicts, setHasActiveConflicts] = useState(false);

  // Inactivation workflow states
  const [showPrimaryInactivateModal, setShowPrimaryInactivateModal] = useState(false);
  const [showSecondaryInactivateModal, setShowSecondaryInactivateModal] = useState(false);
  const [inactivationDependencies, setInactivationDependencies] = useState({});
  const [isInactivating, setIsInactivating] = useState(false);

  // Edit fields state

  const [editName, setEditName] = useState('');
  const [editNameSinhala, setEditNameSinhala] = useState('');
  const [editIsrcCode, setEditIsrcCode] = useState('');
  const [editOther, setEditOther] = useState('');
  const [editOriginalOrVersion, setEditOriginalOrVersion] = useState('Original');
  const [editVersionName, setEditVersionName] = useState('');
  const [editOriginalSongId, setEditOriginalSongId] = useState('');
  const [editSingers, setEditSingers] = useState([]);
  const [editLyricists, setEditLyricists] = useState([]);
  const [editMusicians, setEditMusicians] = useState([]);
  // Main artist IDs for revenue distribution per role
  const [editMainSinger, setEditMainSinger] = useState(null);
  const [editMainLyricist, setEditMainLyricist] = useState(null);
  const [editMainMusician, setEditMainMusician] = useState(null);
  const [editDistributionEnabled, setEditDistributionEnabled] = useState(false);
  const [editSelectedDistribution, setEditSelectedDistribution] = useState('');
  const [editRingtoneEnabled, setEditRingtoneEnabled] = useState(false);
  const [editRingtoneCards, setEditRingtoneCards] = useState([{ provider: '', ringtoneCode: '', contentCode: '' }]);
  const [editArt, setEditArt] = useState(null);
  const [editArtPreview, setEditArtPreview] = useState('');
  const [editTrack, setEditTrack] = useState(null);
  const [editTrackPreview, setEditTrackPreview] = useState('');

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPrimaryActivateModal, setShowPrimaryActivateModal] = useState(false);
  const [showPrimaryDeleteModal, setShowPrimaryDeleteModal] = useState(false);
  const [showSecondaryDeleteModal, setShowSecondaryDeleteModal] = useState(false);
  const [deleteDependencies, setDeleteDependencies] = useState({});
  const [isDeletingSong, setIsDeletingSong] = useState(false);
  const [distributionOptions, setDistributionOptions] = useState([]);
  const [ringtoneOptions, setRingtoneOptions] = useState([]);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  useEffect(() => {
    if (editDistributionEnabled && distributionOptions.length === 0) {
      const fetchDistributors = async () => {
        try {
          const distData = await apiClient('/distributors?limit=1000&status=1');
          if (distData && distData.distributors) {
            setDistributionOptions(distData.distributors.map((distributor) => createDistributorOption(distributor)).filter(Boolean));
          }
        } catch (err) {
          console.warn('Failed to load distributors list:', err);
        }
      };
      fetchDistributors();
    }
  }, [editDistributionEnabled, distributionOptions.length]);

  useEffect(() => {
    if (editRingtoneEnabled && ringtoneOptions.length === 0) {
      const fetchRingtones = async () => {
        try {
          const ringData = await apiClient('/ringtones?limit=1000&status=1');
          if (ringData && ringData.ringtones) {
            setRingtoneOptions(ringData.ringtones.map(r => ({
              value: String(r.id),
              label: r.name
            })));
          }
        } catch (err) {
          console.warn('Failed to load ringtones list:', err);
        }
      };
      fetchRingtones();
    }
  }, [editRingtoneEnabled, ringtoneOptions.length]);

  const fetchSongDetails = useCallback(async () => {
    try {
      const data = await apiClient(`/songs/${song.id}`);
      setDetailedSong(data);

      // Load active conflicts presence for tab warning badge
      const confData = await apiClient(`/songs/${song.id}/conflicts`);
      if (confData && confData.conflicts) {
        const activeCount = confData.conflicts.filter(c => c.status === 1).length;
        setHasActiveConflicts(activeCount > 0);
      }
    } catch (err) {
      console.error('Failed to load song details:', err);
      setDetailedSong(song); // Fallback to list song prop
    }
  }, [song]);

  // Initial fetch on mount / song change
  useEffect(() => {
    fetchSongDetails();
  }, [song]);

  // Re-fetch fresh data whenever the user navigates back to Basic Details
  const handleTabSelect = (tab) => {
    if (tab === 'Basic Details') {
      fetchSongDetails();
    }
    setActiveTab(tab);
  };



  const handleEditClick = () => {
    if (!detailedSong) return;
    setEditName(detailedSong.name || '');
    setEditNameSinhala(detailedSong.nameSinhala || '');
    setEditIsrcCode(detailedSong.isrcCode || '');
    setEditOther(detailedSong.other || '');
    setEditOriginalOrVersion(detailedSong.versionType || 'Original');
    setEditVersionName(detailedSong.versionName || '');
    setEditOriginalSongId(detailedSong.originalSongId ? String(detailedSong.originalSongId) : '');
    setEditSingers(detailedSong.singers || []);
    setEditLyricists(detailedSong.lyricists || []);
    setEditMusicians(detailedSong.musicians || []);
    // Initialize main artist IDs — from saved data or default to first selected
    setEditMainSinger(
      detailedSong.mainSingerId ? String(detailedSong.mainSingerId)
        : (detailedSong.singers && detailedSong.singers.length > 0 ? String(detailedSong.singers[0]) : null)
    );
    setEditMainLyricist(
      detailedSong.mainLyricistId ? String(detailedSong.mainLyricistId)
        : (detailedSong.lyricists && detailedSong.lyricists.length > 0 ? String(detailedSong.lyricists[0]) : null)
    );
    setEditMainMusician(
      detailedSong.mainMusicianId ? String(detailedSong.mainMusicianId)
        : (detailedSong.musicians && detailedSong.musicians.length > 0 ? String(detailedSong.musicians[0]) : null)
    );
    setEditDistributionEnabled(!!detailedSong.distributionProvider);
    setEditSelectedDistribution(detailedSong.distributorId ? String(detailedSong.distributorId) : '');
    setEditRingtoneEnabled(!!(detailedSong.ringtones && detailedSong.ringtones.length > 0));
    setEditRingtoneCards(
      detailedSong.ringtones && detailedSong.ringtones.length > 0
        ? detailedSong.ringtones.map(r => ({ provider: r.ringintoneId || '', ringtoneCode: r.ringtoneCode || '', contentCode: r.contentCode || '' }))
        : [{ provider: '', ringtoneCode: '', contentCode: '' }]
    );
    setEditArt(null);
    setEditArtPreview(detailedSong.imageUrl || '');
    setEditTrack(null);
    setEditTrackPreview(detailedSong.trackUrl || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const proceedSaveSong = async () => {
    try {
      const formData = new FormData();
      formData.append('name', editName.trim());
      formData.append('nameSinhala', editNameSinhala.trim());
      formData.append('isrcCode', editIsrcCode.trim());
      formData.append('other', editOther.trim());
      formData.append('versionType', editOriginalOrVersion);

      if (editTrack) {
        formData.append('track', editTrack);
      }
      if (editArt) {
        formData.append('art', editArt);
      }

      formData.append('artists', JSON.stringify(editSingers));
      formData.append('lyrics', JSON.stringify(editLyricists));
      formData.append('music', JSON.stringify(editMusicians));

      // Main artist IDs for revenue distribution per role
      if (editMainSinger) formData.append('mainSinger', editMainSinger);
      if (editMainLyricist) formData.append('mainLyricist', editMainLyricist);
      if (editMainMusician) formData.append('mainMusician', editMainMusician);

      if (editOriginalOrVersion === 'Version') {
        formData.append('versionDetails', JSON.stringify({
          versionName: editVersionName,
          originalSong: editOriginalSongId
        }));
      }

      if (editDistributionEnabled) {
        formData.append('distribution', JSON.stringify({
          provider: editSelectedDistribution
        }));
      } else {
        formData.append('distribution', 'null');
      }

      if (editRingtoneEnabled) {
        formData.append('ringtones', JSON.stringify(editRingtoneCards));
      } else {
        formData.append('ringtones', 'null');
      }

      const updated = await apiClient(`/songs/${detailedSong.id}`, {
        method: 'PUT',
        body: formData,
      });

      // Update parent listing properties directly to avoid stale listings data
      song.name = updated.name;
      song.nameSinhala = updated.nameSinhala;
      song.artist = updated.artist;
      song.lyrics = updated.lyrics;
      song.music = updated.music;
      song.status = updated.status;
      song.trackUrl = updated.trackUrl;
      song.imageUrl = updated.imageUrl;

      toast.success('Song details updated successfully!');
      setDetailedSong(updated);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update song details:', err);
      toast.error('Failed to update song details.');
    }
  };

  const handleSaveSong = async () => {
    // Validations (loaded from translation file)
    const hasTrack = editTrack || editTrackPreview;
    if (!hasTrack) {
      toast.warning(t('songs.validationTrackRequired'));
      return;
    }
    if (!editNameSinhala.trim()) {
      toast.warning(t('songs.validationSinhalaNameRequired'));
      return;
    }
    if (!editName.trim()) {
      toast.warning(t('songs.validationNameRequired'));
      return;
    }
    if (editSingers.length === 0) {
      toast.warning(t('songs.validationArtistRequired'));
      return;
    }
    if (!editIsrcCode.trim()) {
      toast.warning('ISRC Code is required');
      return;
    }
    if (!editOriginalOrVersion) {
      toast.warning(t('songs.validationTypeRequired'));
      return;
    }

    if (editOriginalOrVersion === 'Version') {
      if (!editVersionName.trim()) {
        toast.warning(t('songs.validationVersionNameRequired'));
        return;
      }
      if (!editOriginalSongId) {
        toast.warning(t('songs.validationOriginalSongRequired'));
        return;
      }
    }

    // Ringtone validation when toggle is active
    if (editRingtoneEnabled) {
      if (editRingtoneCards.length === 0) {
        toast.warning('At least one ringtone entry is required when ringtone is enabled');
        return;
      }
      for (let i = 0; i < editRingtoneCards.length; i++) {
        const card = editRingtoneCards[i];
        if (!card.provider) {
          toast.warning(`Ringtone ${i + 1}: Please select a ringtone provider`);
          return;
        }
        if (!card.ringtoneCode.trim()) {
          toast.warning(`Ringtone ${i + 1}: Ringtone Code is required`);
          return;
        }
        if (!card.contentCode.trim()) {
          toast.warning(`Ringtone ${i + 1}: Content Code is required`);
          return;
        }
      }
      // Check for duplicate providers
      const providers = editRingtoneCards.map(c => c.provider);
      const uniqueProviders = new Set(providers);
      if (uniqueProviders.size !== providers.length) {
        toast.warning('Duplicate ringtone providers are not allowed. Each provider can only be added once.');
        return;
      }
    }

    // Check same song name on the backend database
    try {
      const response = await apiClient(CHECK_SONG_NAME_URL, {
        method: 'POST',
        body: { name: editName.trim(), excludeId: detailedSong.id }
      });
      if (response && response.exists) {
        setShowConfirmModal(true);
      } else {
        await proceedSaveSong();
      }
    } catch (err) {
      console.warn('Song duplicate name check failed:', err);
      await proceedSaveSong();
    }
  };

  const handleConfirmDuplicate = async () => {
    setShowConfirmModal(false);
    await proceedSaveSong();
  };

  const handleCancelDuplicate = () => {
    setShowConfirmModal(false);
  };

  // Helper to reliably test if status represents Active
  const checkIsActive = (statusVal) => {
    if (statusVal === null || statusVal === undefined) return true;
    if (typeof statusVal === 'boolean') return statusVal;
    if (typeof statusVal === 'number') return statusVal === 1;
    const str = String(statusVal).trim().toLowerCase();
    return str === 'active' || str === '1' || str === 'true';
  };

  // ─── INACTIVATION & ACTIVATION WORKFLOW ───
  const targetSong = detailedSong || song;
  const isSongActive = checkIsActive(targetSong?.status);

  const handleDeactivateClick = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const currentSongObj = detailedSong || song;
    if (!currentSongObj) return;

    const currentlyActive = checkIsActive(currentSongObj.status);

    if (!currentlyActive) {
      // Activate flow -> Open confirmation modal
      setShowPrimaryActivateModal(true);
    } else {
      // Primary inactivation confirmation modal
      setShowPrimaryInactivateModal(true);
    }
  };

  const handleConfirmActivate = async () => {
    setShowPrimaryActivateModal(false);
    setIsInactivating(true);
    const currentSongObj = detailedSong || song;
    if (!currentSongObj) return;

    try {
      await apiClient(ACTIVATE_SONG_URL(currentSongObj.id), { method: 'POST' });
      toast.success(`Song "${currentSongObj.name}" has been activated successfully.`);
      if (detailedSong) setDetailedSong({ ...detailedSong, status: 'Active' });
      if (song) song.status = 'Active';
    } catch (err) {
      console.error('Failed to activate song:', err);
      toast.error('Failed to activate song: ' + (err.message || ''));
    } finally {
      setIsInactivating(false);
    }
  };

  const handleConfirmPrimaryInactivate = async () => {
    setShowPrimaryInactivateModal(false);
    setIsInactivating(true);
    const currentSongObj = detailedSong || song;
    if (!currentSongObj) return;

    try {
      // Check active dependencies across all 8 tables
      const checkRes = await apiClient(INACTIVATE_CHECK_SONG_URL(currentSongObj.id));
      if (checkRes && checkRes.hasActiveDependencies) {
        setInactivationDependencies(checkRes.dependencies || {});
        setShowSecondaryInactivateModal(true);
      } else {
        // No active dependent records -> proceed directly to inactivate
        await executeSongInactivation();
      }
    } catch (err) {
      console.error('Inactivation check failed:', err);
      toast.error('Failed to check song dependencies: ' + (err.message || ''));
    } finally {
      setIsInactivating(false);
    }
  };

  const executeSongInactivation = async () => {
    setIsInactivating(true);
    const currentSongObj = detailedSong || song;
    if (!currentSongObj) return;

    try {
      await apiClient(INACTIVATE_SONG_URL(currentSongObj.id), { method: 'POST' });
      toast.success(`Song "${currentSongObj.name}" and all related active records have been set to Inactive successfully.`);
      if (detailedSong) setDetailedSong({ ...detailedSong, status: 'Inactive' });
      if (song) song.status = 'Inactive';
      setShowSecondaryInactivateModal(false);
    } catch (err) {
      console.error('Inactivation failed:', err);
      toast.error('Failed to inactivate song: ' + (err.message || ''));
    } finally {
      setIsInactivating(false);
    }
  };

  // ─── DELETION WORKFLOW ───
  const handleDeleteClick = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setShowPrimaryDeleteModal(true);
  };

  const handleConfirmPrimaryDelete = async () => {
    setShowPrimaryDeleteModal(false);
    setIsDeletingSong(true);
    const currentSongObj = detailedSong || song;
    if (!currentSongObj) return;

    try {
      const checkRes = await apiClient(`/songs/${currentSongObj.id}/delete-check`);
      if (checkRes && checkRes.hasActiveDependencies) {
        setDeleteDependencies(checkRes.dependencies || {});
        setShowSecondaryDeleteModal(true);
      } else {
        await executeSongDeletion();
      }
    } catch (err) {
      console.error('Delete check failed:', err);
      toast.error('Failed to check song dependencies: ' + (err.message || ''));
    } finally {
      setIsDeletingSong(false);
    }
  };

  const executeSongDeletion = async () => {
    setIsDeletingSong(true);
    const currentSongObj = detailedSong || song;
    if (!currentSongObj) return;

    try {
      await apiClient(`/songs/${currentSongObj.id}`, { method: 'DELETE' });
      toast.success(`Song "${currentSongObj.name}" and all associated records deleted successfully.`);
      setShowSecondaryDeleteModal(false);
      onBack();
    } catch (err) {
      console.error('Failed to delete song:', err);
      toast.error('Failed to delete song: ' + (err.message || ''));
    } finally {
      setIsDeletingSong(false);
    }
  };


  const tabs = [
    'Basic Details',
    'Artists & ownership',
    'Albums & Labels',
    'Distribution',
    'Ringtone',
    'Versions',
    'Cases and Notes',
    'Conflicts',
  ];

  if (!detailedSong) {
    return <div className="details-loading-placeholder" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading song details...</div>;
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Basic Details':
        return (
          <BasicDetailsTab
            song={detailedSong}
            isEditing={isEditing}
            editName={editName}
            setEditName={setEditName}
            editNameSinhala={editNameSinhala}
            setEditNameSinhala={setEditNameSinhala}
            editIsrcCode={editIsrcCode}
            setEditIsrcCode={setEditIsrcCode}
            editOther={editOther}
            setEditOther={setEditOther}
            editOriginalOrVersion={editOriginalOrVersion}
            setEditOriginalOrVersion={setEditOriginalOrVersion}
            editVersionName={editVersionName}
            setEditVersionName={setEditVersionName}
            editOriginalSongId={editOriginalSongId}
            setEditOriginalSongId={setEditOriginalSongId}
            editSingers={editSingers}
            setEditSingers={setEditSingers}
            editLyricists={editLyricists}
            setEditLyricists={setEditLyricists}
            editMusicians={editMusicians}
            setEditMusicians={setEditMusicians}
            editMainSinger={editMainSinger}
            setEditMainSinger={setEditMainSinger}
            editMainLyricist={editMainLyricist}
            setEditMainLyricist={setEditMainLyricist}
            editMainMusician={editMainMusician}
            setEditMainMusician={setEditMainMusician}
            editDistributionEnabled={editDistributionEnabled}
            setEditDistributionEnabled={setEditDistributionEnabled}
            editSelectedDistribution={editSelectedDistribution}
            setEditSelectedDistribution={setEditSelectedDistribution}
            editRingtoneEnabled={editRingtoneEnabled}
            setEditRingtoneEnabled={setEditRingtoneEnabled}
            editRingtoneCards={editRingtoneCards}
            setEditRingtoneCards={setEditRingtoneCards}
            editTrack={editTrack}
            setEditTrack={setEditTrack}
            editTrackPreview={editTrackPreview}
            setEditTrackPreview={setEditTrackPreview}
            distributionOptions={distributionOptions}
            ringtoneOptions={ringtoneOptions}
            onDeactivate={handleDeactivateClick}
            isSongActive={isSongActive}
            isInactivating={isInactivating}
          />
        );
      case 'Artists & ownership':
        return <ArtistsOwnershipTab song={detailedSong} />;
      case 'Albums & Labels':
        return <AlbumsLabelsTab song={detailedSong} onSelectAlbum={onSelectAlbum} />;
      case 'Distribution':
        return <DistributionTab song={detailedSong} />;
      case 'Ringtone':
        return <RingtoneTab song={detailedSong} />;
      case 'Versions':
        return <VersionsTab song={detailedSong} />;
      case 'Cases and Notes':
        return <CasesNotesTab song={detailedSong || song} onSelectCase={onSelectCase} />;
      case 'Conflicts':
        return <ConflictsTab song={detailedSong} onConflictsChange={setHasActiveConflicts} />;
      default:
        return (
          <div className="details-placeholder-tab-panel">
            <h3>{activeTab}</h3>
            <p>No content has been added to this section yet.</p>
          </div>
        );
    }
  };

  return (
    <div className="song-details-page">
      {/* Breadcrumbs Navigation */}
      <Breadcrumbs songName={detailedSong.name} onBack={onBack} />

      {/* Waveform Player & Artwork Cover Card */}
      <PlayerCard
        song={detailedSong}
        isEditing={isEditing}
        editArtPreview={editArtPreview}
        setEditArtPreview={setEditArtPreview}
        setEditArt={setEditArt}
      />

      {/* Tabs Switcher Navigation Bar */}
      <TabRow tabs={tabs} activeTab={activeTab} onTabSelect={handleTabSelect} hasActiveConflicts={hasActiveConflicts} />

      {/* Active Tab Metadata Details Card Panel */}
      <div className="details-content-card">
        {renderTabContent()}

        {/* Footer actions block (Edit Song / Deactivate) */}
        {activeTab === 'Basic Details' && (
          <div className="details-content-footer">
            <span className="version-type-label">
              {detailedSong.versionType === 'Version' ? 'Version' : 'Original'}
            </span>

            <div className="details-actions">
              {isEditing ? (
                <>
                  <button className="details-edit-btn" onClick={handleSaveSong} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                      <polyline points="17 21 17 13 7 13 7 21" />
                      <polyline points="7 3 7 8 15 8" />
                    </svg>
                    <span>Save</span>
                  </button>
                  <button className="details-deactivate-btn" onClick={handleCancelEdit} style={{ cursor: 'pointer', backgroundColor: '#e2e8f0', color: '#475569', border: '1px solid #cbd5e1' }}>
                    <span>Cancel</span>
                  </button>
                </>
              ) : (
                <>
                  <button 
                    className="details-edit-btn" 
                    onClick={handleEditClick} 
                    disabled={!isSongActive}
                    title={!isSongActive ? 'Inactive songs cannot be edited' : 'Edit song details'}
                    style={{ 
                      cursor: !isSongActive ? 'not-allowed' : 'pointer',
                      opacity: !isSongActive ? 0.5 : 1
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                    <span>Edit Song</span>
                  </button>

                  <button 
                    className="details-deactivate-btn" 
                    onClick={handleDeactivateClick} 
                    disabled={isInactivating}
                    style={{ 
                      cursor: isInactivating ? 'not-allowed' : 'pointer',
                      backgroundColor: isSongActive ? '#ef4444' : '#10b981',
                      borderColor: isSongActive ? '#dc2626' : '#059669',
                      color: '#ffffff'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                      <line x1="12" y1="2" x2="12" y2="12" />
                    </svg>
                    <span>{isSongActive ? 'Deactivate' : 'Activate'}</span>
                  </button>

                  {!isSongActive && (
                    <button 
                      className="details-deactivate-btn" 
                      onClick={handleDeleteClick} 
                      disabled={isDeletingSong}
                      style={{ 
                        cursor: isDeletingSong ? 'not-allowed' : 'pointer',
                        backgroundColor: '#dc2626',
                        borderColor: '#b91c1c',
                        color: '#ffffff'
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                      <span>Delete</span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Duplicate Name Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        title={t('songs.confirmDuplicateTitle')}
        message={t('songs.confirmDuplicateMessage')}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleConfirmDuplicate}
        onCancel={handleCancelDuplicate}
      />

      {/* Primary Activate Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPrimaryActivateModal}
        type="success"
        title="Activate Song"
        message={`Are you sure you want to mark the song "${detailedSong?.name}" as Active?`}
        confirmLabel="Activate Song"
        cancelLabel="Cancel"
        onConfirm={handleConfirmActivate}
        onCancel={() => setShowPrimaryActivateModal(false)}
      />

      {/* Primary Inactivation Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPrimaryInactivateModal}
        type="warning"
        title="Inactivate Song"
        message={`Are you sure you want to mark the song "${detailedSong?.name}" as Inactive?`}
        confirmLabel={t('common.confirm') || 'Confirm'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        onConfirm={handleConfirmPrimaryInactivate}
        onCancel={() => setShowPrimaryInactivateModal(false)}
      />

      {/* Primary Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPrimaryDeleteModal}
        type="danger"
        title="Delete Song"
        message={`Are you sure you want to delete the song "${detailedSong?.name}"?`}
        confirmLabel="Delete Song"
        cancelLabel="Cancel"
        onConfirm={handleConfirmPrimaryDelete}
        onCancel={() => setShowPrimaryDeleteModal(false)}
      />

      {/* Secondary Confirmation Modal (Inactivation Dependency Warning) */}
      {showSecondaryInactivateModal && (
        <div className="modal-backdrop-overlay" style={{ zIndex: 10000 }}>
          <div className="modal-card-container" style={{ width: '560px', maxWidth: '95vw', padding: '24px' }} role="dialog" aria-modal="true">
            <button 
              className="modal-close-icon-btn" 
              onClick={() => setShowSecondaryInactivateModal(false)}
              aria-label="Close dialog"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="modal-content-wrapper">
              <div className="modal-icon-container warning">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div className="modal-text-content">
                <h3 className="modal-title-heading">Inactivate Song Warning</h3>
                <p className="modal-description-text" style={{ color: '#d97706', fontWeight: '600' }}>
                  The following active records are currently linked to this song across platform tabs:
                </p>
              </div>
            </div>

            <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
              Proceeding will set this song to Inactive. All linked records listed below across each platform module will also be marked as Inactive upon confirmation:
            </div>

            <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                { key: 'singers', label: 'Singers' },
                { key: 'lyrics', label: 'Lyrics (Lyricists)' },
                { key: 'musicians', label: 'Musicians' },
                { key: 'albums', label: 'Albums' },
                { key: 'distributors', label: 'Distributors' },
                { key: 'ringtones', label: 'Ringtones' },
                { key: 'ownerships', label: 'Ownerships' },
                { key: 'conflicts', label: 'Conflicts' },
              ].map(({ key, label }) => {
                const items = inactivationDependencies[key] || [];
                if (items.length === 0) return null;
                const displayItems = items.slice(0, 4);
                const hiddenCount = items.length - 4;

                return (
                  <div key={key} style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{label}</span>
                      <span style={{ fontSize: '11px', fontWeight: '600', backgroundColor: '#e2e8f0', color: '#475569', padding: '2px 8px', borderRadius: '12px' }}>
                        {items.length} {items.length === 1 ? 'record' : 'records'}
                      </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '8px' }}>
                      {displayItems.map((item, idx) => (
                        <div key={item.id || idx} style={{ fontSize: '12.5px', color: '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#0b66e3', display: 'inline-block' }}></span>
                          <span>{item.name}</span>
                        </div>
                      ))}
                      {hiddenCount > 0 && (
                        <div style={{ fontSize: '12px', fontStyle: 'italic', color: '#64748b', marginTop: '2px', paddingLeft: '11px' }}>
                          + {hiddenCount} more {label.toLowerCase()} records...
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="modal-footer-actions">
              <button 
                type="button" 
                className="modal-btn modal-btn-cancel" 
                onClick={() => setShowSecondaryInactivateModal(false)}
                disabled={isInactivating}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="modal-btn modal-btn-confirm warning" 
                onClick={executeSongInactivation}
                disabled={isInactivating}
              >
                {isInactivating ? 'Inactivating...' : 'Confirm Inactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Secondary Confirmation Modal (Delete Dependency Warning) */}
      {showSecondaryDeleteModal && (
        <div className="modal-backdrop-overlay" style={{ zIndex: 10000 }}>
          <div className="modal-card-container" style={{ width: '560px', maxWidth: '95vw', padding: '24px' }} role="dialog" aria-modal="true">
            <button 
              className="modal-close-icon-btn" 
              onClick={() => setShowSecondaryDeleteModal(false)}
              aria-label="Close dialog"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="modal-content-wrapper">
              <div className="modal-icon-container danger">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </div>
              <div className="modal-text-content">
                <h3 className="modal-title-heading">Delete Song Warning</h3>
                <p className="modal-description-text" style={{ color: '#ef4444', fontWeight: '600' }}>
                  The following linked records will also be soft-deleted with this song across platform tabs:
                </p>
              </div>
            </div>

            <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
              Proceeding will delete this song. All linked records listed below across each platform module will also be marked as deleted upon confirmation:
            </div>

            <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                { key: 'singers', label: 'Singers' },
                { key: 'lyrics', label: 'Lyrics (Lyricists)' },
                { key: 'musicians', label: 'Musicians' },
                { key: 'albums', label: 'Albums' },
                { key: 'distributors', label: 'Distributors' },
                { key: 'ringtones', label: 'Ringtones' },
                { key: 'ownerships', label: 'Ownerships' },
                { key: 'conflicts', label: 'Conflicts' },
              ].map(({ key, label }) => {
                const items = deleteDependencies[key] || [];
                if (items.length === 0) return null;
                const displayItems = items.slice(0, 4);
                const hiddenCount = items.length - 4;

                return (
                  <div key={key} style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{label}</span>
                      <span style={{ fontSize: '11px', fontWeight: '600', backgroundColor: '#e2e8f0', color: '#475569', padding: '2px 8px', borderRadius: '12px' }}>
                        {items.length} {items.length === 1 ? 'record' : 'records'}
                      </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '8px' }}>
                      {displayItems.map((item, idx) => (
                        <div key={item.id || idx} style={{ fontSize: '12.5px', color: '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#ef4444', display: 'inline-block' }}></span>
                          <span>{item.name}</span>
                        </div>
                      ))}
                      {hiddenCount > 0 && (
                        <div style={{ fontSize: '12px', fontStyle: 'italic', color: '#64748b', marginTop: '2px', paddingLeft: '11px' }}>
                          + {hiddenCount} more {label.toLowerCase()} records...
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="modal-footer-actions">
              <button 
                type="button" 
                className="modal-btn modal-btn-cancel" 
                onClick={() => setShowSecondaryDeleteModal(false)}
                disabled={isDeletingSong}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="modal-btn modal-btn-confirm danger" 
                onClick={executeSongDeletion}
                disabled={isDeletingSong}
                style={{ backgroundColor: '#ef4444', borderColor: '#dc2626' }}
              >
                {isDeletingSong ? 'Deleting...' : 'Yes, Delete Song'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SongDetails;

